// ---------------------------------------------------------------------------
// Motion: small animations that show what just happened (no library). Started once by the app shell, it keeps up
// with what React renders, like autoIcons:
//  - key figures count up to their value (and from the old to the new value when it changes);
//  - "Loading…" lines shimmer;
//  - a file turning Verified and a deal reaching a new stage flash once;
//  - the bell rings when a new notification arrives.
// `celebrate()` shows confetti and a message (deal won, quota reached, all clear). Nothing moves for people who
// turned animations off on their device (prefers-reduced-motion): they see the end state at once.
// ---------------------------------------------------------------------------
import { ICONS } from './autoIcons';

/** An icon of the CRM's family as an element, for the pop-ups and small animations made here (outside React). */
function drawIcon(name: string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'ic');
  svg.setAttribute('aria-hidden', 'true');
  const attrs: [string, string][] = [['fill', 'none'], ['stroke', 'currentColor'], ['stroke-width', '1.75'], ['stroke-linecap', 'round'], ['stroke-linejoin', 'round']];
  for (const [k, v] of attrs) svg.setAttribute(k, v);
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', (ICONS[name] ?? ICONS.file!).d);
  svg.append(path);
  return svg;
}

export const reducedMotion = (): boolean => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Figures that count up: KPI values, goal tiles, ring centres, column totals. */
const COUNT_SEL = [
  '.kpi-value', '.goal-tile b', '.pipe-kpis b', '.ring-text b', '.ic-tile > b', '.ic-health-tile b', '.ic-mini-item b',
  '.board .col > header .pill', '.board .col > header .num', '.metric b', '.intel-kpi b', '.qb-total b',
  '.att-num', '.att-count', '.lg-tab > b', '.live-count', '.ceo-num', '.ceo-counter b',
  // Figure tiles on Projects, Finance, Odoo and the CEO page (label + bold figure).
  '.prj-kpis > div > b',
].join(', ');
/** Status changes that flash once: [element, class it gains, flash class]. */
const FLASHES: [string, string, string][] = [
  ['.doc-status', 'verified', 'just-verified'],
  ['.path li', 'current', 'just-current'],
  ['.pill', 'appr-approved', 'just-approved'],
  ['.doc-status', 'failed', 'shake'],
];

const NUM = /-?\d[\d,]*(?:\.\d+)?/;
const written = new WeakMap<Text, string>();
const shown = new WeakMap<Element, number>();
const running = new WeakMap<Element, number>();
/** The number currently on screen while counting. */
const onScreen = new WeakMap<Element, number>();

function parse(text: string): { value: number; decimals: number; commas: boolean; index: number; raw: string } | null {
  const m = NUM.exec(text);
  if (!m) return null;
  const raw = m[0];
  const value = Number(raw.replace(/,/g, ''));
  if (!Number.isFinite(value)) return null;
  return { value, decimals: raw.includes('.') ? raw.split('.')[1]!.length : 0, commas: raw.includes(','), index: m.index, raw };
}

function fmt(n: number, decimals: number, commas: boolean): string {
  const s = n.toFixed(decimals);
  if (!commas) return s;
  const [i, d] = s.split('.');
  return `${i!.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${d ? `.${d}` : ''}`;
}

/** Counts the element's first number from `from` to its current value, writing only its single text node. */
function countUp(el: Element, from?: number) {
  if (reducedMotion()) return;
  const node = el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE ? (el.firstChild as Text) : null;
  if (!node) return;
  const target = node.nodeValue ?? '';
  const p = parse(target);
  if (!p) return;
  // A year ("2026") or a code isn't a quantity: never count it.
  if (!p.commas && !p.decimals && p.value >= 1990 && p.value <= 2100) { shown.set(el, p.value); return; }
  const prev = running.get(el);
  // Interrupted (the value changed again mid-count): carry on from the number on screen, no jump.
  const start = prev != null ? (onScreen.get(el) ?? from ?? 0) : from ?? 0;
  shown.set(el, p.value);
  if (start === p.value || p.value === 0) return;
  // A figure that changed while on screen: green when it went up, red when it went down.
  if (from != null) flash(el, p.value > from ? 'num-up' : 'num-down');
  if (prev) cancelAnimationFrame(prev);
  const t0 = performance.now();
  const dur = 500;
  const step = (t: number) => {
    // React replaced the text: stop, the new value is handled on its own.
    if (node.parentNode !== el) return;
    const k = Math.min(1, (t - t0) / dur);
    const e = 1 - Math.pow(1 - k, 3);
    const now = start + (p.value - start) * e;
    onScreen.set(el, now);
    const text = k >= 1 ? target : target.slice(0, p.index) + fmt(now, p.decimals, p.commas) + target.slice(p.index + p.raw.length);
    written.set(node, text);
    node.nodeValue = text;
    if (k < 1) running.set(el, requestAnimationFrame(step)); else running.delete(el);
  };
  running.set(el, requestAnimationFrame(step));
}

function scan(root: Element) {
  const els = root.matches(COUNT_SEL) ? [root] : [];
  root.querySelectorAll(COUNT_SEL).forEach((e) => els.push(e));
  for (const el of els) if (!shown.has(el)) countUp(el);
  // "Loading…" lines shimmer while they wait.
  const loads: Element[] = root.matches('p, span, div') && root.childElementCount === 0 ? [root] : [];
  root.querySelectorAll('p.muted, span.muted, p.act-empty, .muted.pad').forEach((e) => { if (e.childElementCount === 0) loads.push(e); });
  for (const el of loads) checkLoading(el);
}

const LOADING = /^(Loading|Checking|Reading|Searching)\b.*…$/;
/** Shimmer while the text says it is loading; back to normal as soon as the text changes. */
function checkLoading(el: Element) {
  const on = el.childElementCount === 0 && LOADING.test((el.textContent ?? '').trim());
  if (on !== el.classList.contains('is-loading')) el.classList.toggle('is-loading', on);
}

/** Plays a one-off animation class. Removed now and added two frames later (no forced layout, so flashing many
 *  rows at once stays cheap); taken off again after the animation. */
function flash(el: Element, cls: string) {
  if (reducedMotion()) return;
  el.classList.remove(cls);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    el.classList.add(cls);
    window.setTimeout(() => el.classList.remove(cls), 1600);
  }));
}

// --- Rows: a row added later (a record just created or edited) glows; a row moved by sorting slides in. -------
/** List rows the motion layer follows. */
const ROW_SEL = 'tbody > tr, .acts > li, .list-card-rows > li, .att-tiles > .att-tile, .bell-pop li, .lg-gaps > li';
const seenRows = new WeakSet<Element>();
const listBorn = new WeakMap<Element, number>();
/** A detail row opened under another (one cell over the whole width) is not a new record. */
const isDetailRow = (el: Element) => el.tagName === 'TR' && el.children.length === 1 && ((el.firstElementChild as HTMLTableCellElement | null)?.colSpan ?? 1) > 1;

function markRows(root: Element) {
  const rows = root.matches(ROW_SEL) ? [root] : [];
  root.querySelectorAll(ROW_SEL).forEach((r) => rows.push(r));
  for (const r of rows) {
    seenRows.add(r);
    if (r.parentElement && !listBorn.has(r.parentElement)) listBorn.set(r.parentElement, Date.now());
  }
}

// --- Save buttons: a spinner while saving, a green "done" flash after. ------------------------------------
const BUSY = /\b\w+ing\b[^…]*…$/;
const busyBtns = new WeakSet<Element>();
function checkBusy(btn: Element | null) {
  if (!btn) return;
  const busy = (btn as HTMLButtonElement).disabled && BUSY.test((btn.textContent ?? '').trim());
  if (busy) { if (!busyBtns.has(btn)) { busyBtns.add(btn); btn.classList.add('is-busy'); } return; }
  if (!busyBtns.has(btn)) return;
  busyBtns.delete(btn);
  btn.classList.remove('is-busy');
  // Saved (no error message next to it): a short green flash. Checked a moment later, as an error message can
  // appear just after the button comes back.
  window.setTimeout(() => {
    if (!btn.isConnected) return;
    const scope = btn.closest('form, .modal, .panel, section');
    if (!scope?.querySelector('.form-error')) flash(btn, 'btn-done');
  }, 120);
}

/** Starts the motion layer; returns the stop function. */
export function startMotion(): () => void {
  let lastBadge = document.querySelector('.bell .badge')?.textContent ?? '';
  // A required field left empty, or a wrong value: the field shakes when the form is sent.
  const onInvalid = (e: Event) => {
    const el = e.target as Element;
    flash(el, 'shake');
    const field = el.closest('.field, label');
    if (field && field !== el) flash(field, 'shake');
  };
  document.addEventListener('invalid', onInvalid, true);
  // An export to Excel / CSV: the file drops into the corner.
  const onClick = (e: MouseEvent) => {
    const b = (e.target as Element | null)?.closest?.('button, a');
    if (b && !b.closest('.tour, .gs') && /\b(Export|Download)\b/i.test(b.textContent ?? '')) fileDrop(b);
  };
  document.addEventListener('click', onClick, true);
  // Hover hints: an element's title shows as a small box that fades in (mouse only; the title comes back on leave).
  let tipEl: HTMLElement | null = null;
  let tipFor: HTMLElement | null = null;
  let tipTimer = 0;
  const hideTip = () => {
    window.clearTimeout(tipTimer);
    if (tipFor && tipFor.dataset.tip != null) { if (!tipFor.hasAttribute('title')) tipFor.setAttribute('title', tipFor.dataset.tip); delete tipFor.dataset.tip; }
    tipFor = null;
    tipEl?.remove(); tipEl = null;
  };
  const onOver = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    const t = (e.target as Element | null)?.closest?.('[title]') as HTMLElement | null;
    if (!t || t === tipFor || t.closest('svg, .tour, iframe')) return;
    const text = t.getAttribute('title');
    if (!text) return;
    hideTip();
    tipFor = t;
    t.dataset.tip = text;
    t.removeAttribute('title');
    tipTimer = window.setTimeout(() => {
      if (tipFor !== t || !t.isConnected) return;
      const r = t.getBoundingClientRect();
      tipEl = document.createElement('div');
      tipEl.className = 'hover-tip';
      tipEl.setAttribute('role', 'tooltip');
      tipEl.textContent = text;
      document.body.append(tipEl);
      const w = tipEl.offsetWidth;
      tipEl.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2))}px`;
      tipEl.style.top = `${r.bottom + 8 + tipEl.offsetHeight > window.innerHeight ? r.top - tipEl.offsetHeight - 8 : r.bottom + 8}px`;
    }, 350);
  };
  const onOut = (e: PointerEvent) => { if (tipFor && !tipFor.contains(e.relatedTarget as Node | null)) hideTip(); };
  document.addEventListener('pointerover', onOver);
  document.addEventListener('pointerout', onOut);
  window.addEventListener('scroll', hideTip, { passive: true });
  document.addEventListener('pointerdown', hideTip);
  markRows(document.body);
  const observer = new MutationObserver((list) => {
    // New rows per list in this batch (a few = a record added; many = the list re-drawn by a filter).
    const fresh = new Map<Element, Element[]>();
    // Rows put back in another place by sorting (React re-inserts all but one, so only a few = a real move).
    const moved = new Map<Element, Element[]>();
    for (const m of list) {
      if (m.type === 'childList') {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== Node.ELEMENT_NODE) return;
          const el = n as Element;
          scan(el);
          // An error message appearing shakes once.
          if (el.matches('.form-error')) flash(el, 'shake'); else el.querySelectorAll('.form-error').forEach((x) => flash(x, 'shake'));
          if (el.matches(ROW_SEL) && el.parentElement) {
            if (seenRows.has(el)) { moved.set(el.parentElement, [...(moved.get(el.parentElement) ?? []), el]); return; }
            if (!isDetailRow(el)) fresh.set(el.parentElement, [...(fresh.get(el.parentElement) ?? []), el]);
          }
          markRows(el);
        });
        // A number's text node replaced inside a figure.
        const t = m.target as Element;
        if (t.nodeType === Node.ELEMENT_NODE && t.matches?.(COUNT_SEL) && shown.has(t)) countUp(t, shown.get(t));
        if (t.nodeType === Node.ELEMENT_NODE && t.classList?.contains('is-loading')) checkLoading(t);
        if (t.nodeType === Node.ELEMENT_NODE) checkBusy(t.closest?.('button.btn') ?? null);
      } else if (m.type === 'characterData') {
        const node = m.target as Text;
        const el = node.parentElement;
        if (!el || written.get(node) === node.nodeValue) continue;
        if (el.classList.contains('is-loading') || el.matches('p, span, div')) checkLoading(el);
        if (el.matches(COUNT_SEL)) countUp(el, shown.get(el));
        checkBusy(el.closest('button.btn'));
      } else if (m.type === 'attributes') {
        const el = m.target as Element;
        if (m.attributeName === 'disabled') { if (el.matches('button.btn')) checkBusy(el); continue; }
        for (const [sel, gains, cls] of FLASHES) {
          if (el.matches(sel) && el.classList.contains(gains) && !(m.oldValue ?? '').split(/\s+/).includes(gains)) flash(el, cls);
        }
      }
    }
    for (const [parent, rows] of fresh) {
      const born = listBorn.get(parent);
      if (born != null && Date.now() - born > 1500 && rows.length <= 2) rows.forEach((r) => flash(r, 'row-new'));
    }
    for (const rows of moved.values()) if (rows.length <= 2) rows.forEach((r) => flash(r, 'row-moved'));
    // The bell rings when the unread count goes up.
    const badge = document.querySelector('.bell .badge')?.textContent ?? '';
    if (badge !== lastBadge) {
      const more = (Number.parseInt(badge, 10) || 0) > (Number.parseInt(lastBadge, 10) || 0);
      lastBadge = badge;
      const bell = document.querySelector('.bell');
      if (more && bell) flash(bell, 'ringing');
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled'], attributeOldValue: true });
  scan(document.body);
  return () => {
    observer.disconnect(); hideTip();
    document.removeEventListener('invalid', onInvalid, true); document.removeEventListener('click', onClick, true);
    document.removeEventListener('pointerover', onOver); document.removeEventListener('pointerout', onOut); window.removeEventListener('scroll', hideTip); document.removeEventListener('pointerdown', hideTip);
  };
}

// ---------------------------------------------------------------------------
// Celebrations
// ---------------------------------------------------------------------------

const COLORS = ['#0b5cab', '#0b2f5b', '#f5b301', '#7fb2ea', '#16a34a', '#ffffff'];

/** Confetti and a short message: a deal won, the quota reached, all of today's tasks done. Shown only to this person. */
export function celebrate(kind: 'won' | 'quota' | 'clear', text?: string) {
  if (typeof document === 'undefined') return;
  const message = text ?? (kind === 'won' ? 'Deal won!' : kind === 'quota' ? 'Quota reached! Well done' : 'All clear for today');
  const icon = kind === 'clear' ? 'check' : 'trophy';
  const wrap = document.createElement('div');
  wrap.className = `celebrate ${kind}`;
  wrap.setAttribute('aria-hidden', 'true');
  const toast = document.createElement('div');
  toast.className = 'celebrate-toast';
  toast.setAttribute('role', 'status');
  const ico = document.createElement('span');
  ico.className = 'celebrate-ico';
  ico.append(drawIcon(icon));
  const label = document.createElement('b');
  toast.append(ico, label);
  document.body.append(wrap, toast);
  // The text goes in after the status box is on the page, so screen readers announce it.
  window.setTimeout(() => { label.textContent = message; }, 50);
  const end = () => { wrap.remove(); toast.remove(); };
  if (kind !== 'clear') buzz([80, 40, 120]);
  if (reducedMotion() || kind === 'clear') { window.setTimeout(end, 2600); return; }

  const canvas = document.createElement('canvas');
  wrap.append(canvas);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = window.innerWidth, h = window.innerHeight;
  canvas.width = w * dpr; canvas.height = h * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) { window.setTimeout(end, 2600); return; }
  ctx.scale(dpr, dpr);
  const pieces = Array.from({ length: kind === 'quota' ? 180 : 130 }, (_, i) => ({
    x: w / 2 + (Math.random() - 0.5) * 120, y: h * 0.38,
    vx: (Math.random() - 0.5) * 16, vy: -Math.random() * 15 - 5,
    r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
    s: 6 + Math.random() * 6, c: COLORS[i % COLORS.length]!,
  }));
  const t0 = performance.now();
  const frame = (t: number) => {
    const age = t - t0;
    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = Math.max(0, 1 - Math.max(0, age - 1800) / 700);
    for (const p of pieces) {
      p.vy += 0.42; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.r += p.vr;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r);
      ctx.fillStyle = p.c; ctx.strokeStyle = 'rgba(11,47,91,.25)'; ctx.lineWidth = 0.5;
      ctx.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2); ctx.strokeRect(-p.s / 2, -p.s / 4, p.s, p.s / 2);
      ctx.restore();
    }
    if (age < 2500) requestAnimationFrame(frame); else end();
  };
  requestAnimationFrame(frame);
}

/** Celebrates once per key (stored in this browser), e.g. the quota for one quarter. */
export function celebrateOnce(key: string, kind: 'won' | 'quota' | 'clear', text?: string) {
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch { /* storage unavailable: celebrate anyway */ }
  celebrate(kind, text);
}

/** A paper plane leaves the element and flies off to the top right (a quotation sent on to sales). */
export function paperPlane(from: Element) {
  if (typeof document === 'undefined' || reducedMotion() || !('animate' in Element.prototype)) return;
  const r = from.getBoundingClientRect();
  const el = document.createElement('div');
  el.className = 'fly-plane';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = '<svg viewBox="0 0 24 24"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/></svg>';
  el.style.left = `${r.left + r.width / 2 - 14}px`;
  el.style.top = `${r.top - 8}px`;
  document.body.append(el);
  const dx = window.innerWidth - r.left, dy = -(r.top + 80);
  const a = el.animate([
    { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
    { transform: 'translate(50px, -36px) rotate(-12deg) scale(1.15)', opacity: 1, offset: 0.35 },
    { transform: `translate(${dx}px, ${dy}px) rotate(-22deg) scale(.6)`, opacity: 0 },
  ], { duration: 1150, easing: 'cubic-bezier(.4, 0, .2, 1)' });
  a.onfinish = () => el.remove();
}

/** Something sent on: a small card flies from one element to another, which then pulses (a deal handed off). */
export function flyTo(from: Element | null, to: Element | null, icon = 'file') {
  if (typeof document === 'undefined' || !from || !to) return;
  if (reducedMotion() || !('animate' in Element.prototype)) { flash(to, 'landed'); return; }
  const a = from.getBoundingClientRect(), b = to.getBoundingClientRect();
  const el = document.createElement('div');
  el.className = 'fly-card';
  el.setAttribute('aria-hidden', 'true');
  el.append(drawIcon(icon));
  el.style.left = `${a.left + a.width / 2 - 16}px`;
  el.style.top = `${a.top + a.height / 2 - 16}px`;
  document.body.append(el);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const anim = el.animate([
    { transform: 'translate(0, 0) scale(1)', opacity: 1 },
    { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 60}px) scale(1.15)`, opacity: 1, offset: 0.5 },
    { transform: `translate(${dx}px, ${dy}px) scale(.5)`, opacity: 0.2 },
  ], { duration: 900, easing: 'cubic-bezier(.4, 0, .2, 1)' });
  anim.onfinish = () => { el.remove(); flash(to, 'landed'); };
}

/** An Excel / CSV export: a small file drops into a "downloaded" tray at the bottom right. */
function fileDrop(from: Element) {
  if (reducedMotion() || !('animate' in Element.prototype)) return;
  const r = from.getBoundingClientRect();
  const file = document.createElement('div');
  file.className = 'fly-file';
  file.setAttribute('aria-hidden', 'true');
  file.textContent = 'CSV';
  file.style.left = `${r.left + r.width / 2 - 14}px`;
  file.style.top = `${r.top}px`;
  document.body.append(file);
  const tx = window.innerWidth - 70 - (r.left + r.width / 2 - 14), ty = window.innerHeight - 70 - r.top;
  const a = file.animate([
    { transform: 'translate(0, 0) scale(1)', opacity: 1 },
    { transform: `translate(${tx * 0.5}px, ${Math.min(ty * 0.5, -40)}px) scale(1.1)`, opacity: 1, offset: 0.4 },
    { transform: `translate(${tx}px, ${ty}px) scale(.7)`, opacity: 1 },
  ], { duration: 800, easing: 'cubic-bezier(.4, 0, .6, 1)' });
  a.onfinish = () => {
    file.remove();
    const tray = document.createElement('div');
    tray.className = 'dl-tray';
    tray.setAttribute('aria-hidden', 'true');
    tray.append(drawIcon('download'), ' Downloaded');
    document.body.append(tray);
    window.setTimeout(() => tray.remove(), 1800);
  };
}

/** A big green check that pops in the middle of the screen and fades (a site check-in saved). */
export function checkBurst() {
  if (typeof document === 'undefined') return;
  const el = document.createElement('div');
  el.className = 'check-burst';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = '<svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="24"/><path d="M15 27l7 7 15-16"/></svg>';
  document.body.append(el);
  window.setTimeout(() => el.remove(), reducedMotion() ? 600 : 1100);
}

// --- Messages after an action ------------------------------------------------------------------------------
/** A short message that slides in at the bottom and slides away ("Deal saved", "Task created", "Copied"). */
/**
 * A short message that slides in at the bottom ("Deal saved"). With `undo`, it carries an Undo button for 6 s;
 * with `hint`, it is a next-step suggestion (blue, 5 s). The same message twice is shown once (an action's own
 * message with Undo replaces the plain one the save already showed).
 */
/**
 * Closes with a short animation: adds .closing (styles.css says how it leaves), then calls done when it has played.
 * Without animations on this device, or without the element, done runs at once.
 */
export function closeAnimated(el: Element | null | undefined, done: () => void, ms = 200) {
  if (!el || reducedMotion() || el.classList.contains('closing')) { if (!el?.classList.contains('closing')) done(); return; }
  el.classList.add('closing');
  let finished = false;
  const finish = () => { if (finished) return; finished = true; done(); };
  el.addEventListener('animationend', finish, { once: true });
  setTimeout(finish, ms + 80);
}

export function toast(text: string, opts: { undo?: () => void | Promise<unknown>; hint?: boolean; action?: { label: string; run: () => void } } = {}) {
  if (typeof document === 'undefined') return;
  let stack = document.querySelector<HTMLElement>('.toast-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.className = 'toast-stack';
    stack.setAttribute('role', 'status');
    stack.setAttribute('aria-live', 'polite');
    document.body.append(stack);
  }
  const label = opts.hint ? `→ ${text}` : `✓ ${text}`;
  const same = [...stack.children].find((c) => (c as HTMLElement).dataset.text === label) as HTMLElement | undefined;
  if (same && !opts.undo) return;
  same?.remove();
  const t = document.createElement('div');
  t.className = `toast-msg${opts.hint ? ' hint' : ''}`;
  t.dataset.text = label;
  const span = document.createElement('span');
  span.textContent = label;
  t.append(span);
  const close = () => { t.classList.add('out'); window.setTimeout(() => t.remove(), reducedMotion() ? 0 : 260); };
  const button = (name: string, run: () => void) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'toast-btn';
    b.textContent = name;
    b.addEventListener('click', () => { close(); run(); });
    t.append(b);
  };
  if (opts.undo) {
    const undo = opts.undo;
    button('Undo', () => { void Promise.resolve(undo()).then(() => toast('Undone')).catch(() => toast('Could not undo')); });
    t.classList.add('with-btn');
  }
  if (opts.action) { button(opts.action.label, opts.action.run); t.classList.add('with-btn'); }
  stack.append(t);
  while (stack.children.length > 3) stack.firstElementChild?.remove();
  window.setTimeout(close, opts.undo ? 6000 : opts.hint ? 5000 : 2600);
}

/** A short buzz on phones (deal won, task done, check-in). Ignored where the device can't vibrate. */
export function buzz(pattern: number | number[] = 30) {
  try { navigator.vibrate?.(pattern); } catch { /* not supported */ }
}

/** A clickable counter: scrolls to its list (element id, or the element) and highlights it for a moment. */
export function jumpTo(id: string | Element) {
  const el = typeof id === 'string' ? document.getElementById(id) : id;
  if (!el) return;
  // A hidden section (Customize) or an empty one (nothing to list): nowhere to go.
  const target = (el as HTMLElement).offsetHeight > 0 ? el : null;
  if (!target) return;
  // Land just below the CRM's top bar (it stays on screen while scrolling), not under it.
  const bars = [...document.querySelectorAll<HTMLElement>('.topbar, .login-as-bar')].reduce((h, b) => h + b.getBoundingClientRect().height, 0);
  const top = target.getBoundingClientRect().top + window.scrollY - bars - 12;
  window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion() ? 'auto' : 'smooth' });
  flash(target, 'jump-flash');
}

/** When the last task of the day was ticked off on Home (shared between the task rows and the list). */
let lastDoneAt = 0;
export const markTaskDone = () => { lastDoneAt = Date.now(); buzz(30); };
export const justFinishedTasks = () => Date.now() - lastDoneAt < 6000;
